/**
 * new-game-setup.ts — full-screen DOM overlay for new-game setup (v2M1).
 *
 * Per UI_UX §9.2. Two-column form: Galaxy settings left (system count, seed)
 * and Empires right (player race + AI opponent slots). Start Game collects
 * the chosen options and hands them to the session bootstrapper.
 *
 * Multi-empire same-race is allowed — AI slot dropdowns don't filter out
 * the player's race, and slots don't filter each other. 'Random' slots
 * resolve through the seeded RNG inside scenario.ts so the full new-game
 * state is reproducible from (seed, settings).
 */

import type { LoadedGameData } from '../data/loader';
import type { AiOpponentSpec, NewGameOptions } from '../game/scenario';
import { AI_COLOR_PALETTE, DEFAULT_PLAYER_COLOR } from '../game/scenario';
import {
  EMPIRE_COLOR_PALETTE,
  firstFreePaletteHex,
  paletteHexAt,
} from '../state/empire-colors';
import type {
  AiBehaviour,
  AiDifficulty,
  GalaxyShape,
  PlanetsPerSystemTier,
  RaceId,
} from '../state/types/index';

export interface NewGameSetupCallbacks {
  onStart(options: NewGameOptions): void;
  onBack(): void;
}

export interface NewGameSetupHandle {
  destroy(): void;
}

const SYSTEM_COUNT_MIN = 6;
const SYSTEM_COUNT_MAX = 30;
const SYSTEM_COUNT_DEFAULT = 12;
const AI_COUNT_MAX = 5;
const AI_COUNT_DEFAULT = 2;
/** v4M3.1 — `docs/TEAMS.md §2` caps team count for the picker.
 *  Picker offers `Solo` plus `Team 1`…`Team N`. Eight is far past the
 *  v1 empire roster cap (1 player + 5 AI = 6 slots) and gives slack for
 *  future scenario sizes without re-locking the spec. */
const TEAM_PICKER_MAX = 8;

/**
 * A random 32-bit unsigned integer, rendered as hex. Uses Math.random —
 * seed generation itself does not need to be deterministic, only gameplay
 * after the seed is fixed.
 */
function randomSeedHex(): string {
  const n = Math.floor(Math.random() * 0x100000000);
  return '0x' + n.toString(16).toUpperCase().padStart(8, '0');
}

/** Parse a seed input. Accepts decimal or 0x-prefixed hex. Returns null on invalid. */
function parseSeedInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = trimmed.toLowerCase().startsWith('0x')
    ? parseInt(trimmed.slice(2), 16)
    : Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  return n >>> 0; // force unsigned 32-bit
}

export function mountNewGameSetup(
  parent: HTMLElement,
  data: LoadedGameData,
  callbacks: NewGameSetupCallbacks,
): NewGameSetupHandle {
  // --- state -------------------------------------------------------------
  const v1Races = data.races.filter((r) => r.status === 'v1');
  const defaultPlayerRace = (v1Races[0]?.raceId ?? 'RACE-AA') as RaceId;

  // Drive the dropdown options from the loaded AI data so the xlsx
  // remains the single authoring surface. We retain a canonical display
  // order (Easy/Normal/Hard, behaviours per design lock) and intersect
  // with the keys actually present in the data.
  // v4M8.1a — difficulty + archetype options updated for the
  // compositional model (AI.md §3 + §2). 'Normal' renamed to 'Medium';
  // 'Brutal' added. Legacy six behaviours retired; new six archetypes
  // (Aggressor / Defender / Expander / Explorer / Diplomat / Researcher).
  const difficultyOrder: readonly AiDifficulty[] = ['Easy', 'Medium', 'Hard', 'Brutal'];
  const orderedDifficulties: AiDifficulty[] = difficultyOrder.filter(
    (k) => k in data.aiBehaviours.difficulties,
  );
  const behaviourOrder: readonly AiBehaviour[] = [
    'Aggressor',
    'Defender',
    'Expander',
    'Explorer',
    'Diplomat',
    'Researcher',
  ];
  const behaviourOptions: AiBehaviour[] = behaviourOrder.filter(
    (b) => b in data.aiBehaviours.behaviours,
  );

  interface AiSlotChoice {
    race: RaceId | 'random';
    difficulty: AiDifficulty;
    behaviour: AiBehaviour;
    /** v4M1.9 — empire colour override. CSS hex `#RRGGBB`. */
    color: string;
    /** v4M3.1 — team membership. `null` = solo (default); same value across multiple slots groups those AIs onto the same team. */
    teamId: number | null;
  }

  let seedText = randomSeedHex();
  let systemCount = SYSTEM_COUNT_DEFAULT;
  // v4M2 — galaxy options. Defaults match v1 behaviour exactly so a
  // player who picks "Start Game" without touching these gets the
  // pre-v4M2 experience.
  let galaxyShape: GalaxyShape = 'even';
  let planetsPerSystemTier: PlanetsPerSystemTier = 'medium';
  let neutralPlanetDensity = 0.30; // matches Balance.xlsx default per `GALAXY.md §3.8.7`
  let playerRace: RaceId = defaultPlayerRace;
  // MFix14 — fixed 8-swatch palette replaces v4M1.9 free RGB picker.
  // Player defaults to palette[0] (Red); AI slots cycle palette[1..N]
  // so the initial assignment is unique-per-slot. Swap-on-conflict (see
  // `claimColor` below) preserves the unique-per-slot invariant when
  // the user clicks a swatch already taken by another slot.
  let playerColor: string = paletteHexAt(0);
  void DEFAULT_PLAYER_COLOR; // retained for backwards-compat reference; unused now.
  // v4M3.1 — player team membership (null = solo). Per-AI-slot team
  // membership lives on `AiSlotChoice.teamId`.
  let playerTeamId: number | null = null;
  let aiCount = AI_COUNT_DEFAULT;
  const aiSlotChoices: AiSlotChoice[] = new Array(AI_COUNT_MAX)
    .fill(null)
    .map((_, i) => ({
      race: 'random',
      // v4M8.1a — 'Normal' renamed to 'Medium'; 'Balanced' dropped (no
      // equivalent in the new 6-archetype set), Aggressor used as the
      // fixture default per balance-ai.ts DEFAULT_AI_ARCHETYPE.
      difficulty: 'Medium',
      behaviour: 'Aggressor',
      // MFix14 — seed colour defaults from the fixed empire palette.
      // Player takes palette[0]; AI slots take palette[i+1] so the
      // first 5 AI slots claim distinct palette entries 1..5 (palette
      // has 8 entries; max AI count is 5 so player + 5 AI = 6 unique).
      color: paletteHexAt(i + 1),
      // v4M3.1 — team membership; null = solo (legacy default).
      teamId: null,
    }));

  // --- overlay skeleton --------------------------------------------------
  const overlay = document.createElement('div');
  overlay.id = 'new-game-setup-overlay';
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    background: '#05060A',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '48px 24px 24px',
    zIndex: '1000',
    fontFamily:
      'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    color: '#D7DEEA',
    overflowY: 'auto',
  } as Partial<CSSStyleDeclaration>);

  const title = document.createElement('div');
  title.textContent = 'NEW GAME';
  Object.assign(title.style, {
    fontSize: '28px',
    letterSpacing: '0.15em',
    fontWeight: '600',
    marginBottom: '32px',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(title);

  const columnsWrap = document.createElement('div');
  Object.assign(columnsWrap.style, {
    display: 'flex',
    gap: '48px',
    width: '100%',
    // v3M7.2.1 — widened from 820 → 1040 so the three-dropdown AI slot
    // row (Race / Difficulty / Behaviour) has room without label clipping.
    maxWidth: '1040px',
    justifyContent: 'center',
    alignItems: 'flex-start',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(columnsWrap);

  // --- column factory ----------------------------------------------------
  function makeColumn(heading: string): HTMLElement {
    const col = document.createElement('div');
    Object.assign(col.style, {
      flex: '1 1 0',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      padding: '20px',
      background: '#0E131C',
      border: '1px solid #2A3344',
      borderRadius: '2px',
      minWidth: '320px',
    } as Partial<CSSStyleDeclaration>);
    const h = document.createElement('div');
    h.textContent = heading;
    Object.assign(h.style, {
      fontSize: '14px',
      letterSpacing: '0.12em',
      color: '#7E8799',
      textTransform: 'uppercase',
      marginBottom: '4px',
    } as Partial<CSSStyleDeclaration>);
    col.appendChild(h);
    columnsWrap.appendChild(col);
    return col;
  }

  // --- label helpers -----------------------------------------------------
  function makeRow(label: string, control: HTMLElement): HTMLElement {
    const row = document.createElement('label');
    Object.assign(row.style, {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      fontSize: '13px',
      color: '#D7DEEA',
    } as Partial<CSSStyleDeclaration>);
    const lbl = document.createElement('span');
    lbl.textContent = label;
    lbl.style.color = '#7E8799';
    row.appendChild(lbl);
    row.appendChild(control);
    return row;
  }

  function styleInput(el: HTMLElement): void {
    Object.assign(el.style, {
      background: '#05060A',
      color: '#D7DEEA',
      border: '1px solid #2A3344',
      borderRadius: '2px',
      padding: '8px 10px',
      fontFamily: 'JetBrains Mono, ui-monospace, monospace',
      fontSize: '13px',
    } as Partial<CSSStyleDeclaration>);
  }

  /**
   * v4M3.1 — build a Team-picker `<select>` element. Options:
   *   `Solo` (value = `''`, maps to `null`),
   *   `Team 1`..`Team N` (value = stringified team-id integer).
   * Caller supplies the current value + change handler. Per
   * `docs/TEAMS.md §2`, "Team N" labels grow with the player's
   * picks; we expose the full range up to `TEAM_PICKER_MAX` so the
   * dropdown shape is stable regardless of current assignments.
   */
  function makeTeamSelect(
    initial: number | null,
    onChange: (teamId: number | null) => void,
  ): HTMLSelectElement {
    const sel = document.createElement('select');
    sel.title =
      'Team membership. Solo = no team. Picking the same "Team N" for two or more empires groups them — teammates share vision, cannot fire on each other, and win/lose together.';
    styleInput(sel);
    const soloOpt = document.createElement('option');
    soloOpt.value = '';
    soloOpt.textContent = 'Solo';
    sel.appendChild(soloOpt);
    for (let i = 1; i <= TEAM_PICKER_MAX; i++) {
      const opt = document.createElement('option');
      opt.value = String(i);
      opt.textContent = `Team ${i}`;
      sel.appendChild(opt);
    }
    sel.value = initial === null ? '' : String(initial);
    sel.addEventListener('change', () => {
      const raw = sel.value;
      onChange(raw === '' ? null : Number(raw));
    });
    return sel;
  }

  // --- Galaxy column -----------------------------------------------------
  const galaxyCol = makeColumn('Galaxy');

  // System count slider
  const systemCountVal = document.createElement('span');
  systemCountVal.textContent = String(systemCount);
  const systemCountSlider = document.createElement('input');
  systemCountSlider.type = 'range';
  systemCountSlider.min = String(SYSTEM_COUNT_MIN);
  systemCountSlider.max = String(SYSTEM_COUNT_MAX);
  systemCountSlider.value = String(systemCount);
  systemCountSlider.style.width = '100%';
  systemCountSlider.title = `Total number of systems in the galaxy (${SYSTEM_COUNT_MIN}–${SYSTEM_COUNT_MAX}). More systems means a bigger map and a longer game.`;
  systemCountSlider.addEventListener('input', () => {
    systemCount = Number(systemCountSlider.value);
    systemCountVal.textContent = String(systemCount);
  });
  const systemCountRow = makeRow('Systems', systemCountSlider);
  const sysLabelSpan = systemCountRow.querySelector('span')!;
  sysLabelSpan.textContent = 'Systems';
  sysLabelSpan.appendChild(document.createTextNode('  '));
  sysLabelSpan.appendChild(systemCountVal);
  (systemCountVal.style as unknown as Record<string, string>).color = '#D7DEEA';
  galaxyCol.appendChild(systemCountRow);

  // Seed input + randomise
  const seedWrap = document.createElement('div');
  Object.assign(seedWrap.style, {
    display: 'flex',
    gap: '8px',
  } as Partial<CSSStyleDeclaration>);
  const seedInput = document.createElement('input');
  seedInput.type = 'text';
  seedInput.value = seedText;
  seedInput.title = 'Galaxy seed — same seed + same settings produces the same map. Hex (e.g. 1A2B) or decimal.';
  styleInput(seedInput);
  seedInput.style.flex = '1';
  seedInput.addEventListener('input', () => {
    seedText = seedInput.value;
  });
  const randomiseBtn = document.createElement('button');
  randomiseBtn.type = 'button';
  randomiseBtn.textContent = 'Randomise';
  randomiseBtn.title = 'Replace the seed with a fresh random hex value.';
  styleInput(randomiseBtn);
  randomiseBtn.style.cursor = 'pointer';
  randomiseBtn.addEventListener('click', () => {
    seedText = randomSeedHex();
    seedInput.value = seedText;
  });
  seedWrap.appendChild(seedInput);
  seedWrap.appendChild(randomiseBtn);
  galaxyCol.appendChild(makeRow('Seed (hex or decimal)', seedWrap));

  // v4M2.2 — galaxy shape picker. Authoritative spec
  // `docs/GALAXY.md §3.8`. Four options; `even` is the v1 default.
  const galaxyShapeSelect = document.createElement('select');
  galaxyShapeSelect.title =
    'Galaxy shape. Even = uniform-jitter (v1 default). Spiral = arms (3/4/5 by system count). Bottleneck = two clusters joined by a Gaia-rich choke. Core and Rim = dense inner + sparse outer ring.';
  styleInput(galaxyShapeSelect);
  const SHAPE_LABELS: ReadonlyArray<{ value: GalaxyShape; label: string }> = [
    { value: 'even', label: 'Even (default)' },
    { value: 'spiral', label: 'Spiral' },
    { value: 'bottleneck', label: 'Bottleneck' },
    { value: 'core-and-rim', label: 'Core and Rim' },
  ];
  for (const s of SHAPE_LABELS) {
    const opt = document.createElement('option');
    opt.value = s.value;
    opt.textContent = s.label;
    if (s.value === galaxyShape) opt.selected = true;
    galaxyShapeSelect.appendChild(opt);
  }
  galaxyShapeSelect.addEventListener('change', () => {
    galaxyShape = galaxyShapeSelect.value as GalaxyShape;
  });
  galaxyCol.appendChild(makeRow('Galaxy shape', galaxyShapeSelect));

  // v4M2.1 — planets-per-system tier. `docs/GALAXY.md §3.8.6`.
  const planetsPerSystemSelect = document.createElement('select');
  planetsPerSystemSelect.title =
    'Planets per system. Low = 1–2 (sparse). Medium = 1–3 (v1 default). High = 2–4 (dense). Bottleneck choke always has 3.';
  styleInput(planetsPerSystemSelect);
  const TIER_LABELS: ReadonlyArray<{ value: PlanetsPerSystemTier; label: string }> = [
    { value: 'low', label: 'Low (1–2)' },
    { value: 'medium', label: 'Medium (1–3)' },
    { value: 'high', label: 'High (2–4)' },
  ];
  for (const t of TIER_LABELS) {
    const opt = document.createElement('option');
    opt.value = t.value;
    opt.textContent = t.label;
    if (t.value === planetsPerSystemTier) opt.selected = true;
    planetsPerSystemSelect.appendChild(opt);
  }
  planetsPerSystemSelect.addEventListener('change', () => {
    planetsPerSystemTier = planetsPerSystemSelect.value as PlanetsPerSystemTier;
  });
  galaxyCol.appendChild(makeRow('Planets per system', planetsPerSystemSelect));

  // v4M2.3 — neutral density slider. 0..0.6 in 0.05 steps per
  // `docs/GALAXY.md §3.8.7`. Default seeded from the Balance.xlsx
  // value (0.30) at module-init time.
  const neutralDensityVal = document.createElement('span');
  neutralDensityVal.textContent = `${Math.round(neutralPlanetDensity * 100)}%`;
  const neutralDensitySlider = document.createElement('input');
  neutralDensitySlider.type = 'range';
  neutralDensitySlider.min = '0';
  neutralDensitySlider.max = '0.6';
  neutralDensitySlider.step = '0.05';
  neutralDensitySlider.value = String(neutralPlanetDensity);
  neutralDensitySlider.style.width = '100%';
  neutralDensitySlider.title =
    'Fraction of non-homeworld habitable planets seeded as populated neutral worlds. 0% = no neutrals (no diplomacy targets); 60% = diplomacy-heavy. Eligibility filters by biome; only worlds with defensive-structure entries roll.';
  neutralDensitySlider.addEventListener('input', () => {
    neutralPlanetDensity = Number(neutralDensitySlider.value);
    neutralDensityVal.textContent = `${Math.round(neutralPlanetDensity * 100)}%`;
  });
  const neutralRow = makeRow('Neutral density', neutralDensitySlider);
  const neutralLabelSpan = neutralRow.querySelector('span')!;
  neutralLabelSpan.textContent = 'Neutral density';
  neutralLabelSpan.appendChild(document.createTextNode('  '));
  neutralLabelSpan.appendChild(neutralDensityVal);
  (neutralDensityVal.style as unknown as Record<string, string>).color = '#D7DEEA';
  galaxyCol.appendChild(neutralRow);

  // --- Empires column ----------------------------------------------------
  // v3M7.2.1 — widen the empires column relative to the galaxy column so
  // the three-dropdown AI slot row fits without clipping.
  const empiresCol = makeColumn('Empires');
  Object.assign(empiresCol.style, {
    flex: '2 1 0',
    minWidth: '560px',
  } as Partial<CSSStyleDeclaration>);

  // MFix14 — fixed 8-swatch empire colour picker. Replaces the v4M1.9
  // HTML5 input[type=color] free-RGB picker. Two motivations: predictable
  // tint behaviour against the new author-baked colour-ring sprites
  // (Option C path), and combat readability via designer-curated
  // contrast. The picker enforces a unique-per-slot colour invariant via
  // `claimColor`: clicking a swatch already taken by another slot bumps
  // that slot to the first free palette hex (`firstFreePaletteHex`).
  //
  // Architecture: each picker registers a `refresh` callback at mount
  // time. `claimColor` mutates the state then calls every registered
  // refresh so all visible swatch grids re-render their selected-state
  // borders in lockstep. Avoids prop-drilling and keeps the per-slot
  // makeColorInput call sites unchanged in shape.
  type ColorSlotId = 'player' | `ai-${number}`;

  function getSlotColor(slotId: ColorSlotId): string {
    if (slotId === 'player') return playerColor;
    const idx = Number(slotId.slice('ai-'.length));
    return aiSlotChoices[idx]!.color;
  }

  function setSlotColor(slotId: ColorSlotId, hex: string): void {
    if (slotId === 'player') {
      playerColor = hex;
      return;
    }
    const idx = Number(slotId.slice('ai-'.length));
    aiSlotChoices[idx]!.color = hex;
  }

  /** All visible slot ids (player + AI slots up to aiCount). */
  function visibleSlots(): ColorSlotId[] {
    const ids: ColorSlotId[] = ['player'];
    for (let i = 0; i < aiCount; i++) ids.push(`ai-${i}`);
    return ids;
  }

  /** Refresh callbacks indexed by slot id; populated by `makeColorInput`. */
  const colorPickerRefreshers = new Map<ColorSlotId, () => void>();

  /**
   * Click handler. If another visible slot already holds `hex`, that
   * slot bumps to the first free palette hex (excluding `hex` itself
   * and every other currently-taken slot). Then `targetSlot` gets
   * `hex`. Finally every registered picker re-renders so the new
   * selected-state borders show.
   *
   * When the entire palette is occupied (>8 visible slots — currently
   * impossible at AI_COUNT_MAX = 5, but defensive for future cap
   * raises), the bumped slot stays on its current colour (silent no-op
   * — UI doesn't surface this case since it can't happen at current
   * caps; revisit if the AI cap is raised past `palette.length - 1`).
   */
  function claimColor(targetSlot: ColorSlotId, hex: string): void {
    const slots = visibleSlots();
    for (const other of slots) {
      if (other === targetSlot) continue;
      if (getSlotColor(other).toUpperCase() === hex.toUpperCase()) {
        // Find first free hex excluding `hex` (the target's new colour)
        // and every other slot's current colour.
        const taken = slots
          .filter((s) => s !== other) // exclude self so we don't conflict with our own pre-bump colour
          .map((s) => getSlotColor(s));
        taken.push(hex); // also exclude what we're about to set on target
        const free = firstFreePaletteHex(taken);
        if (free !== null) setSlotColor(other, free);
        break;
      }
    }
    setSlotColor(targetSlot, hex);
    for (const refresh of colorPickerRefreshers.values()) refresh();
  }

  function makeColorInput(slotId: ColorSlotId): HTMLElement {
    const wrap = document.createElement('div');
    Object.assign(wrap.style, {
      display: 'inline-grid',
      gridTemplateColumns: 'repeat(8, 18px)',
      gap: '3px',
      padding: '3px',
      border: '1px solid #2A3344',
      borderRadius: '2px',
      background: '#0F1320',
    } as Partial<CSSStyleDeclaration>);

    const swatchEls: HTMLButtonElement[] = [];
    for (const entry of EMPIRE_COLOR_PALETTE) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.title = `${entry.label} — click to claim. If another empire has this colour they swap to a free one.`;
      Object.assign(btn.style, {
        width: '18px',
        height: '18px',
        padding: '0',
        border: '2px solid transparent',
        borderRadius: '2px',
        background: entry.hex,
        cursor: 'pointer',
        boxSizing: 'border-box',
      } as Partial<CSSStyleDeclaration>);
      btn.addEventListener('click', () => {
        claimColor(slotId, entry.hex);
      });
      swatchEls.push(btn);
      wrap.appendChild(btn);
    }

    function refresh(): void {
      const current = getSlotColor(slotId).toUpperCase();
      EMPIRE_COLOR_PALETTE.forEach((entry, i) => {
        const selected = entry.hex.toUpperCase() === current;
        swatchEls[i]!.style.border = selected ? '2px solid #FFFFFF' : '2px solid transparent';
      });
    }
    refresh();
    colorPickerRefreshers.set(slotId, refresh);
    // Defensive cleanup: if the wrap is removed from the DOM (e.g. AI
    // slot hidden via the slider), the entry stays in the registry.
    // That's fine — refresh becomes a no-op cosmetic update on a
    // detached element. The registry resets when the overlay unmounts.

    return wrap;
  }

  // Retained reference so older code paths that import AI_COLOR_PALETTE
  // don't break. The v4M1.9 palette is no longer the source of truth
  // for new-game colour assignment.
  void AI_COLOR_PALETTE;

  // Player race dropdown + colour picker
  const playerSelect = document.createElement('select');
  playerSelect.title = 'Pick your race. Each race has a unique tech path, ship roster, and special abilities.';
  styleInput(playerSelect);
  for (const r of v1Races) {
    const opt = document.createElement('option');
    opt.value = r.raceId;
    opt.textContent = r.name;
    if (r.raceId === playerRace) opt.selected = true;
    playerSelect.appendChild(opt);
  }
  playerSelect.addEventListener('change', () => {
    playerRace = playerSelect.value as RaceId;
  });
  const playerRaceColorWrap = document.createElement('div');
  Object.assign(playerRaceColorWrap.style, {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  } as Partial<CSSStyleDeclaration>);
  playerSelect.style.flex = '1 1 auto';
  playerRaceColorWrap.appendChild(playerSelect);
  playerRaceColorWrap.appendChild(makeColorInput('player'));
  // v4M3.1 — team picker beside race + colour. `null` = Solo default.
  const playerTeamSelect = makeTeamSelect(playerTeamId, (t) => {
    playerTeamId = t;
    refreshTeamSummary();
  });
  playerTeamSelect.style.flex = '0 0 110px';
  playerRaceColorWrap.appendChild(playerTeamSelect);
  empiresCol.appendChild(makeRow('Your race + colour + team', playerRaceColorWrap));

  // v4M3.1 — Team summary line per `docs/TEAMS.md §2`. Renders the
  // current partition (Solo: ... · Team 1: ... etc.) so the player can
  // see the grouping at a glance. Refreshed on every team-picker change
  // and after the AI-count slider remaps which slots are visible.
  const teamSummaryLine = document.createElement('div');
  Object.assign(teamSummaryLine.style, {
    fontSize: '11px',
    letterSpacing: '0.04em',
    color: '#7E8799',
    marginTop: '4px',
    marginBottom: '4px',
    lineHeight: '1.4',
  } as Partial<CSSStyleDeclaration>);
  empiresCol.appendChild(teamSummaryLine);

  /**
   * v4M3.1 — recompute the team-summary text from the current picker
   * state. "Solo" empires are listed first (single comma-joined group);
   * each populated `Team N` follows. Hidden AI slots (index ≥ aiCount)
   * are skipped — they don't seed empires when the game starts.
   */
  function refreshTeamSummary(): void {
    const labels = new Map<string, string[]>();
    const soloLabel = 'Solo';
    function push(group: string, name: string): void {
      let arr = labels.get(group);
      if (!arr) {
        arr = [];
        labels.set(group, arr);
      }
      arr.push(name);
    }
    push(playerTeamId === null ? soloLabel : `Team ${playerTeamId}`, 'You');
    for (let i = 0; i < aiCount; i++) {
      const choice = aiSlotChoices[i]!;
      const group = choice.teamId === null ? soloLabel : `Team ${choice.teamId}`;
      push(group, `AI ${i + 1}`);
    }
    // Order: Solo first, then Team 1..N ascending. Filters empty groups
    // (the soloLabel may be absent entirely if every slot picked a team).
    const segments: string[] = [];
    if (labels.has(soloLabel)) {
      segments.push(`${soloLabel}: ${labels.get(soloLabel)!.join(', ')}`);
    }
    const teamKeys: string[] = [];
    for (const k of labels.keys()) {
      if (k !== soloLabel) teamKeys.push(k);
    }
    teamKeys.sort((a, b) => {
      const na = Number(a.slice(5));
      const nb = Number(b.slice(5));
      return na - nb;
    });
    for (const k of teamKeys) segments.push(`${k}: ${labels.get(k)!.join(', ')}`);
    teamSummaryLine.textContent = segments.length === 0 ? '' : segments.join(' · ');
  }

  // AI opponent count slider
  const aiCountVal = document.createElement('span');
  aiCountVal.textContent = String(aiCount);
  const aiCountSlider = document.createElement('input');
  aiCountSlider.type = 'range';
  aiCountSlider.min = '0';
  aiCountSlider.max = String(AI_COUNT_MAX);
  aiCountSlider.value = String(aiCount);
  aiCountSlider.style.width = '100%';
  aiCountSlider.title = `Number of AI opponents (0–${AI_COUNT_MAX}). Per-slot race / difficulty / behaviour appear below.`;
  const aiCountRow = makeRow('AI opponents', aiCountSlider);
  const aiLabelSpan = aiCountRow.querySelector('span')!;
  aiLabelSpan.textContent = 'AI opponents';
  aiLabelSpan.appendChild(document.createTextNode('  '));
  aiLabelSpan.appendChild(aiCountVal);
  (aiCountVal.style as unknown as Record<string, string>).color = '#D7DEEA';
  empiresCol.appendChild(aiCountRow);

  // Per-slot race dropdowns — show/hide based on aiCount.
  const aiSlotsWrap = document.createElement('div');
  Object.assign(aiSlotsWrap.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  } as Partial<CSSStyleDeclaration>);
  empiresCol.appendChild(aiSlotsWrap);

  /**
   * Wrap a select with a tiny subhead label so the user can tell which
   * column is which at a glance. v3M7.2.1.
   */
  function makeTaggedSelect(tag: string, sel: HTMLSelectElement): HTMLElement {
    const wrap = document.createElement('div');
    Object.assign(wrap.style, {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    } as Partial<CSSStyleDeclaration>);
    const subhead = document.createElement('span');
    subhead.textContent = tag;
    Object.assign(subhead.style, {
      fontSize: '10px',
      letterSpacing: '0.10em',
      color: '#7E8799',
      textTransform: 'uppercase',
    } as Partial<CSSStyleDeclaration>);
    wrap.appendChild(subhead);
    wrap.appendChild(sel);
    return wrap;
  }

  const slotRowEls: HTMLElement[] = [];
  for (let i = 0; i < AI_COUNT_MAX; i++) {
    const slotIndex = i;

    // v4M3.1 — four side-by-side selects: race+colour / difficulty /
    // behaviour / team. Per `docs/TEAMS.md §2` the team picker sits on
    // each empire row. Earlier (v3M7.2.1) the grid was 3-column; the
    // fourth column is `0.9fr` so the picker stays narrower than the
    // race / difficulty / behaviour controls without forcing the row
    // to wrap on the 560px-min Empires column.
    const tripletWrap = document.createElement('div');
    Object.assign(tripletWrap.style, {
      display: 'grid',
      gridTemplateColumns: '1.2fr 1fr 1fr 0.9fr',
      gap: '8px',
    } as Partial<CSSStyleDeclaration>);

    const raceSel = document.createElement('select');
    raceSel.title = 'Race this AI opponent plays. "Random" picks one at game start, biased toward whichever races aren\'t already taken.';
    styleInput(raceSel);
    const randomOpt = document.createElement('option');
    randomOpt.value = 'random';
    randomOpt.textContent = 'Random';
    raceSel.appendChild(randomOpt);
    for (const r of v1Races) {
      const opt = document.createElement('option');
      opt.value = r.raceId;
      opt.textContent = r.name;
      raceSel.appendChild(opt);
    }
    raceSel.value = aiSlotChoices[slotIndex]!.race;
    raceSel.addEventListener('change', () => {
      aiSlotChoices[slotIndex]!.race = raceSel.value as RaceId | 'random';
    });

    // v4M1.9 — colour swatch next to the race select. Wraps both in a
    // horizontal flex so the tagged-select column hosts race + colour
    // without breaking the three-column grid layout of the slot row.
    const raceColorWrap = document.createElement('div');
    Object.assign(raceColorWrap.style, {
      display: 'flex',
      gap: '6px',
      alignItems: 'center',
    } as Partial<CSSStyleDeclaration>);
    raceSel.style.flex = '1 1 auto';
    raceColorWrap.appendChild(raceSel);
    raceColorWrap.appendChild(makeColorInput(`ai-${slotIndex}`));

    const diffSel = document.createElement('select');
    // v4M8.1b — per-difficulty tooltip strings from AI.md §3.
    const difficultyTooltips: Record<AiDifficulty, string> = {
      Easy: 'Easy — slow + resource-disadvantaged. Forgiving for new players.',
      Medium: 'Medium — baseline. AI behaves as its archetype dictates.',
      Hard: 'Hard — faster + resource-bonused. Multi-hop pathing fully utilised.',
      Brutal: 'Brutal — fastest + heavily resource-bonused. Unlocks coordinated multi-fleet assault, pre-emptive recall, strategic Warp Gate positioning, and per-race advanced strategies.',
    };
    diffSel.title = 'AI difficulty — applies multipliers to AI dispatch, risk tolerance, and AI tick rate. Brutal unlocks advanced strategies.';
    styleInput(diffSel);
    for (const d of orderedDifficulties) {
      const opt = document.createElement('option');
      opt.value = d;
      opt.textContent = d;
      opt.title = difficultyTooltips[d];
      diffSel.appendChild(opt);
    }
    diffSel.value = aiSlotChoices[slotIndex]!.difficulty;
    diffSel.addEventListener('change', () => {
      aiSlotChoices[slotIndex]!.difficulty = diffSel.value as AiDifficulty;
      diffSel.title = difficultyTooltips[diffSel.value as AiDifficulty];
    });

    // v4M8.1b — per-archetype tooltip strings from AI.md §2. The picker
    // surfaces a short one-liner per archetype so players can pick an AI
    // personality without consulting the design doc.
    const archetypeTooltips: Record<AiBehaviour, string> = {
      Aggressor: 'Aggressor — combat-forward expansion via conquest. Low patience, fast dispatch, willingness to engage at moderate odds.',
      Defender: 'Defender — fortified, slow expansion, strong home defence. Willing to lose foreign engagements rather than expose homeworld.',
      Expander: 'Expander — colonisation-heavy, fast economic growth. Dispatches Colonisers aggressively; combat passive unless directly threatened.',
      Explorer: 'Explorer — scout-heavy, knowledge-and-trade oriented. Maximises map information; uses Trade diplomacy to grow economy.',
      Diplomat: 'Diplomat — diplomacy-heavy, federation-and-vassal play. Uses Influence accumulation as primary expansion tool; combat only to defend.',
      Researcher: 'Researcher — tech-priority, late-game tech advantage. Prioritises research over expansion; aims to peak-tech then convert advantage to military.',
      Neutral: 'Neutral — synthetic neutral empire (not selectable for AI opponents).',
    };
    const behaveSel = document.createElement('select');
    behaveSel.title = 'AI archetype — picks this opponent\'s strategic personality. Hover an option to see its profile.';
    styleInput(behaveSel);
    for (const b of behaviourOptions) {
      const opt = document.createElement('option');
      opt.value = b;
      opt.textContent = b;
      opt.title = archetypeTooltips[b];
      behaveSel.appendChild(opt);
    }
    behaveSel.value = aiSlotChoices[slotIndex]!.behaviour;
    behaveSel.addEventListener('change', () => {
      aiSlotChoices[slotIndex]!.behaviour = behaveSel.value as AiBehaviour;
      behaveSel.title = archetypeTooltips[behaveSel.value as AiBehaviour];
    });

    // v4M1.9 — pass the race+colour wrap into the tagged-select helper
    // by replicating the wrap pattern inline since makeTaggedSelect
    // expects a select element. Cleanest: tag manually here.
    const raceCol = document.createElement('div');
    Object.assign(raceCol.style, {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    } as Partial<CSSStyleDeclaration>);
    const raceTag = document.createElement('span');
    raceTag.textContent = 'Race + Colour';
    Object.assign(raceTag.style, {
      fontSize: '10px',
      letterSpacing: '0.10em',
      color: '#7E8799',
      textTransform: 'uppercase',
    } as Partial<CSSStyleDeclaration>);
    raceCol.appendChild(raceTag);
    raceCol.appendChild(raceColorWrap);
    tripletWrap.appendChild(raceCol);
    tripletWrap.appendChild(makeTaggedSelect('Difficulty', diffSel));
    tripletWrap.appendChild(makeTaggedSelect('Behaviour', behaveSel));

    // v4M3.1 — team picker. Solo (default) groups solo; same Team N
    // across multiple slots groups those AIs (or the player) together.
    const teamSel = makeTeamSelect(aiSlotChoices[slotIndex]!.teamId, (t) => {
      aiSlotChoices[slotIndex]!.teamId = t;
      refreshTeamSummary();
    });
    tripletWrap.appendChild(makeTaggedSelect('Team', teamSel));

    const row = makeRow(`AI opponent ${i + 1}`, tripletWrap);
    aiSlotsWrap.appendChild(row);
    slotRowEls.push(row);
  }

  const syncSlotVisibility = (): void => {
    for (let i = 0; i < AI_COUNT_MAX; i++) {
      slotRowEls[i]!.style.display = i < aiCount ? '' : 'none';
    }
  };
  aiCountSlider.addEventListener('input', () => {
    aiCount = Number(aiCountSlider.value);
    aiCountVal.textContent = String(aiCount);
    syncSlotVisibility();
    // v4M3.1 — hidden AI slots drop out of the team summary.
    refreshTeamSummary();
  });
  syncSlotVisibility();
  // v4M3.1 — seed the team summary with the initial all-Solo partition.
  refreshTeamSummary();

  // --- footer actions ----------------------------------------------------
  const footer = document.createElement('div');
  Object.assign(footer.style, {
    display: 'flex',
    gap: '12px',
    marginTop: '32px',
    justifyContent: 'center',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(footer);

  function makeActionButton(label: string, primary: boolean): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    Object.assign(btn.style, {
      padding: '12px 32px',
      background: primary ? '#2B4A7A' : '#0E131C',
      color: primary ? '#FFFFFF' : '#D7DEEA',
      border: `1px solid ${primary ? '#3F6FA8' : '#2A3344'}`,
      borderRadius: '2px',
      fontFamily: 'inherit',
      fontSize: '14px',
      letterSpacing: '0.08em',
      cursor: 'pointer',
    } as Partial<CSSStyleDeclaration>);
    return btn;
  }

  const backBtn = makeActionButton('Back', false);
  backBtn.title = 'Return to the main menu without starting a game.';
  backBtn.addEventListener('click', callbacks.onBack);
  footer.appendChild(backBtn);

  const startBtn = makeActionButton('Start Game', true);
  startBtn.title = 'Generate the galaxy with these settings and start playing.';
  footer.appendChild(startBtn);

  // --- error display (for invalid seed etc.) -----------------------------
  const errorLine = document.createElement('div');
  Object.assign(errorLine.style, {
    color: '#C84A4A',
    fontSize: '12px',
    marginTop: '12px',
    minHeight: '16px',
    textAlign: 'center',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(errorLine);

  startBtn.addEventListener('click', () => {
    const seedN = parseSeedInput(seedText);
    if (seedN === null) {
      errorLine.textContent =
        'Invalid seed — enter a decimal integer or 0x-prefixed hex.';
      return;
    }
    errorLine.textContent = '';
    const aiOpponents: AiOpponentSpec[] = [];
    for (let i = 0; i < aiCount; i++) {
      const choice = aiSlotChoices[i]!;
      aiOpponents.push({
        raceId: choice.race,
        difficulty: choice.difficulty,
        behaviour: choice.behaviour,
        color: choice.color,
        // v4M3.1 — per-slot team membership flows to AiOpponentSpec.
        teamId: choice.teamId,
      });
    }
    callbacks.onStart({
      seed: seedN,
      playerRace,
      playerColor,
      systemCount,
      // v4M2 — galaxy options carried through to scenario init.
      galaxyShape,
      planetsPerSystemTier,
      neutralPlanetDensity,
      // v4M3.1 — player team membership carried through to scenario init.
      playerTeamId,
      aiOpponents,
    });
  });

  parent.appendChild(overlay);

  return {
    destroy(): void {
      overlay.remove();
    },
  };
}
